import Colors from '@/constants/Colors';
import { DeleteOutlined, DragOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputNumber, Select, Space, Spin, Tooltip } from 'antd';
import ActivityTypeIcon from '../ActivityTypeIcon/ActivityTypeIcon';
import { StyledTaskActivitiesEditor } from './TaskActivitiesEditor.styled';
import useTaskActivitiesEditor from './useTaskActivitiesEditor.hook';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { getLevelLabel } from '@/constants/ActivityLevelLabels';
import { useMemo } from 'react';
import { getLabelForList } from '@/constants/ActivityTypeLabelsTeacher';
import typeLabel from '@/constants/ActivityTypeLabelsStudents';

export default function TaskActivitiesEditor({
  taskId,
  themeId,
  taskTemplateId,
  taskActivities,
  setTaskActivities,
}) {
  const { state, actions } = useTaskActivitiesEditor({
    taskId,
    themeId,
    taskTemplateId,
    taskActivities,
    setTaskActivities,
  });

  const allActivities = useMemo(
    () => getAllActivities(state.allTaskActivities),
    [state.allTaskActivities]
  );

  return (
    <StyledTaskActivitiesEditor>
      <Spin spinning={state.loading}>
        <div className="activities-container">
          <p>
            <strong>Configure the activities:</strong>
          </p>
          <div className="activities">
            <DragDropContext onDragEnd={actions.onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="draggable-container"
                  >
                    {state.activityTypeConfById &&
                      state.selectedTaskActivities?.map(
                        (taskActivity, index) => {
                          const key = `${taskActivity.id}-${index}`;
                          const activityLabel = getLabelForList({
                            activity: taskActivity.activity,
                            activities: allActivities,
                          });
                          const typeConf =
                            state.activityTypeConfById[taskActivity.activityId];

                          return (
                            <Draggable
                              key={key}
                              draggableId={key}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={provided.draggableProps.style}
                                  className={`activity ${
                                    snapshot.isDragging && 'dragging'
                                  }`}
                                >
                                  <div className="activity-info">
                                    <Space>
                                      <div>
                                        <ActivityTypeIcon
                                          type={taskActivity.activity.type}
                                          withCircle={true}
                                          color="white"
                                          circleColor={
                                            Colors.activityTypeColors[
                                              taskActivity.activity.type
                                            ]
                                          }
                                          size={14}
                                          width={32}
                                          height={32}
                                        />
                                      </div>
                                      <div className="name">
                                        <span>{activityLabel}</span>
                                        <span className="miniactivity">
                                          {getMiniactivityLabel(
                                            taskActivity,
                                            typeConf
                                          )}
                                        </span>
                                      </div>
                                    </Space>
                                    <div className="actions">
                                      <Tooltip title="Drag to sort">
                                        <DragOutlined className="drag-icon" />
                                      </Tooltip>
                                      <Tooltip title="Remove from task">
                                        <Button
                                          shape="circle"
                                          icon={<DeleteOutlined />}
                                          onClick={() =>
                                            actions.onTaskActivityRemoved(index)
                                          }
                                        />
                                      </Tooltip>
                                    </div>
                                  </div>
                                  <div className="options">
                                    {typeConf ? (
                                      <TaskActivityOptions
                                        taskActivity={taskActivity}
                                        setTaskActivityConfOption={(
                                          option,
                                          value
                                        ) =>
                                          actions.setTaskActivityConfOption(
                                            index,
                                            option,
                                            value
                                          )
                                        }
                                        typeConf={typeConf}
                                        selectedTaskActivities={
                                          state.selectedTaskActivities
                                        }
                                      />
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        }
                      )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {state.availableTaskActivities.length ? (
              <>
                <div className="separator">Activities you can add:</div>
                {state.availableTaskActivities?.map((taskActivity, index) => {
                  const typeConf =
                    state.activityTypeConfById[taskActivity.activityId];

                  return (
                    <div key={index} className="activity available">
                      <div className="activity-info">
                        <Space>
                          <div>
                            <ActivityTypeIcon
                              type={taskActivity.activity.type}
                              withCircle={true}
                              color="white"
                              circleColor={
                                Colors.activityTypeColors[
                                  taskActivity.activity.type
                                ]
                              }
                              size={10}
                              width={24}
                              height={24}
                            />
                          </div>
                          <div className="name">
                            <span>
                              {getLabelForList({
                                activity: taskActivity.activity,
                                activities: allActivities,
                              })}
                            </span>
                            <span className="miniactivity">
                              {getMiniactivityLabel(taskActivity, typeConf)}
                            </span>
                          </div>
                        </Space>
                        <Button
                          size="small"
                          shape="circle"
                          icon={<PlusOutlined />}
                          onClick={() =>
                            actions.onTaskActivityAdded(taskActivity)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>
        </div>
      </Spin>
    </StyledTaskActivitiesEditor>
  );
}

function TaskActivityOptions({
  taskActivity,
  setTaskActivityConfOption,
  typeConf,
  selectedTaskActivities,
}) {
  const { activityConf } = taskActivity;

  if (typeConf.miniactivities) {
    typeConf = typeConf.miniactivities.find(
      (miniactivityConf) =>
        miniactivityConf.miniactivityType == activityConf.miniactivityType ||
        (miniactivityConf.default && !activityConf.miniactivityType)
    );
  }

  const allowedLevels = typeConf.options.allowedLevels;
  let disabledLevels = [];
  if (allowedLevels && selectedTaskActivities?.length) {
    const sameTaskActivities = selectedTaskActivities.filter(
      (selectedTaskActivity) =>
        selectedTaskActivity.activityId == taskActivity.activityId &&
        selectedTaskActivity.activityConf.miniactivityType ==
          taskActivity.activityConf.miniactivityType
    );

    disabledLevels = allowedLevels.filter((level) =>
      sameTaskActivities.some(
        (sameTaskActivity) =>
          level != activityConf.options.level &&
          sameTaskActivity.activityConf.options.level == level
      )
    );
  }

  return (
    <ItemOptions
      allowedLevels={allowedLevels}
      disabledLevels={disabledLevels}
      level={activityConf.options.level}
      mode={activityConf.options.mode}
      allowedModes={typeConf.options.allowedModes}
      allowedRepetitions={typeConf.options.repetitions}
      repetitions={activityConf.options.repetitions}
      allowedThemes={typeConf.themes}
      themes={activityConf.themes}
      setOption={setTaskActivityConfOption}
      questionsPerScreen={activityConf.options.questionsPerScreen}
    />
  );
}

function ItemOptions({
  allowedLevels,
  disabledLevels,
  level,
  mode,
  allowedModes,
  repetitions,
  allowedRepetitions,
  allowedThemes,
  themes,
  setOption,
  questionsPerScreen,
}) {
  return (
    <>
      <Space>
        {allowedRepetitions && (
          <Repetitions
            value={repetitions}
            onChange={(value) => {
              setOption('repetitions', value);
            }}
          />
        )}
        {allowedLevels?.length > 1 && (
          <SingleOptionSelector
            values={allowedLevels.map((level) => ({
              label: getLevelLabel(level),
              value: level,
            }))}
            disabledValues={disabledLevels}
            value={level}
            onChange={(value) => {
              setOption('level', value);
            }}
          />
        )}
        {allowedModes?.length > 1 && (
          <SingleOptionSelector
            values={allowedModes.map((mode) => ({
              label: mode,
              value: mode,
            }))}
            value={mode}
            onChange={(value) => {
              setOption('mode', value);
            }}
          />
        )}
      </Space>
      {allowedThemes && (
        <ThemesSelector
          values={allowedThemes}
          value={themes}
          onChange={(value) => {
            setOption('themes', value);
          }}
        />
      )}
      {questionsPerScreen && (
        <InputNumber
          value={questionsPerScreen}
          className="questions-per-screen"
          size="small"
          min={1}
          max={10}
          addonAfter={
            <span className="questions-per-screen">questions per screen</span>
          }
          onChange={(value) => {
            setOption('questionsPerScreen', value);
          }}
        />
      )}
    </>
  );
}

function Repetitions({ value = '1', onChange, disabled }) {
  return (
    <InputNumber
      value={value}
      className="repetitions"
      addonAfter={<span className="repetitions-text">times</span>}
      size="small"
      min={1}
      max={5}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function SingleOptionSelector({
  value,
  values,
  disabledValues,
  onChange,
  disabled,
}) {
  return (
    <Select
      value={value ?? values[0]?.value}
      size="small"
      className="single-option-selector"
      disabled={disabled}
      onChange={onChange}
    >
      {values.map(({ value: optionValue, label }) => (
        <Select.Option
          key={optionValue}
          value={optionValue}
          disabled={disabledValues?.includes(optionValue)}
        >
          {label}
        </Select.Option>
      ))}
    </Select>
  );
}

function ThemesSelector({ value = [], values, onChange }) {
  return (
    <Select
      placeholder="Select one"
      className="w-100"
      size="small"
      mode="tags"
      options={values}
      fieldNames={{
        label: 'label',
        value: 'themeId',
      }}
      value={value.map((valueItem) => valueItem.themeId)}
      showArrow={true}
      onSelect={(_, selected) => {
        onChange([...value, selected]);
      }}
      onDeselect={(deselectedThemeId) =>
        onChange(
          value.filter((valueItem) => valueItem.themeId != deselectedThemeId)
        )
      }
    />
  );
}

function getAllActivities(taskActivities = []) {
  return taskActivities.reduce((acc, taskActivity) => {
    const alreadyAdded = acc.some(
      (activity) => activity.id == taskActivity.activity.id
    );
    if (!alreadyAdded) {
      acc.push(taskActivity.activity);
    }
    return acc;
  }, []);
}

function getMiniactivityLabel(taskActivity, typeConf) {
  const hasDefaultMiniactivity = typeConf?.miniactivities?.some(
    (miniactivityConf) => miniactivityConf.default
  );

  return taskActivity.activityConf.miniactivityType || hasDefaultMiniactivity
    ? typeLabel(
        taskActivity.activity.type,
        taskActivity.activity.data?.subType,
        taskActivity.activityConf.miniactivityType
      )
    : '';
}
